import { MyMealsQuantitySelectionMode } from "../../Account/Subscriptions/subscriptionsUtils/subscriptions-types";
import { RecipeIngredientReplacementMode, } from "../../OrderFlow/OrderFlowProvider/OrderFlowTypes";
import { useCompany } from "../../Shared/components/CompanyProvider/CompanyProvider";
import { tokenReplace } from "../../Shared/utils/tokenReplacement";
/**
 * useMealDay(config, isSelectedDay, companyText, day)
 * * This is a custom hook which calculates the text, icon and status class for a MealDay.
 *
 * @param {object} config
 * @param {array} companyText
 * @param {object} day
 * @param {bool} isSkipped
 * @param {bool} isFutureDay
 */
const useMealDay = (config, companyText, day, isSkipped, isFutureDay) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const { tokenMap } = useCompany();
    const showDayIcons = config.ShowDayIcons || false;
    const totalMealCount = day.Meals.reduce((sum, meal) => sum + meal.Quantity, 0);
    const totalSelectedRecipeCount = day.Recipes.reduce((sum, recipe) => sum + recipe.Quantity, 0);
    const needsRecipeSelections = totalMealCount > 0 && totalSelectedRecipeCount < totalMealCount;
    const anyProductsForDay = day.Products.reduce((sum, product) => sum + product.NewQuantity, 0) > 0;
    const anyMealsForDay = totalMealCount > 0;
    const recipesAvailable = day.Recipes.length > 0;
    const restrictMealQuantitySelection = config.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.NoChanges;
    let icon = "fa-info-circle";
    let text = "";
    if (day.IsDayPastCutOff) {
        text = "Past cutoff";
    }
    else if (day.IsHoliday) {
        text = "Company Holiday";
    }
    else if (needsRecipeSelections) {
        if (recipesAvailable) {
            text = (_b = (_a = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayMessagesAvailable")) === null || _a === void 0 ? void 0 : _a.Text) !== null && _b !== void 0 ? _b : "";
            icon = "fa-exclamation-circle";
        }
        else {
            text = (_d = (_c = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayMessagesNotAvailable")) === null || _c === void 0 ? void 0 : _c.Text) !== null && _d !== void 0 ? _d : "";
        }
    }
    else if (anyMealsForDay) {
        text = (_f = (_e = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayMessagesSelected")) === null || _e === void 0 ? void 0 : _e.Text) !== null && _f !== void 0 ? _f : "";
        icon = "fas fa-check-circle";
    }
    else if (anyProductsForDay) {
        text = (_h = (_g = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayMessagesAddOns")) === null || _g === void 0 ? void 0 : _g.Text) !== null && _h !== void 0 ? _h : "";
    }
    else if (!restrictMealQuantitySelection && !config.RestrictAddRemoveDays) {
        if (isFutureDay) {
            text = (_k = (_j = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayAddFutureMeal")) === null || _j === void 0 ? void 0 : _j.Text) !== null && _k !== void 0 ? _k : "";
        }
        else {
            text = (_m = (_l = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayMessagesBuyMeals")) === null || _l === void 0 ? void 0 : _l.Text) !== null && _m !== void 0 ? _m : "";
        }
    }
    if (isSkipped && !day.IsDayPastCutOff) {
        text = "To add {meal_entity_name_plural}, un-skip this day on the Skip Days page.";
    }
    // Fallback if no other conditions are met.
    if (!text) {
        text = (_p = (_o = companyText === null || companyText === void 0 ? void 0 : companyText.find((text) => text.Title === "MealDayMessagesNotAvailable")) === null || _o === void 0 ? void 0 : _o.Text) !== null && _p !== void 0 ? _p : "";
    }
    let disabled = false;
    let mealsAreSelected = false;
    if (!needsRecipeSelections && anyMealsForDay) {
        mealsAreSelected = true;
    }
    if ((!day.HasMealsOrProducts && !day.Config.CanAddDays) || isSkipped || day.IsDayPastCutOff) {
        disabled = true;
    }
    text = tokenReplace(text, false, tokenMap);
    return {
        icon: showDayIcons ? icon : undefined,
        text,
        disabled,
        mealsAreSelected,
    };
};
export default useMealDay;
export const isEmptyDay = (day) => {
    return !day.Meals.some((m) => m.Quantity > 0) && !day.Products.some((p) => p.NewQuantity > 0);
};
export const toDayCostInfo = (day, recipeIngredientReplacementMode) => {
    return {
        IsNewDay: day.IsNewDay,
        Date: day.DateFormatted,
        DeliveryDate: day.ShipDate,
        Meals: day.Meals,
        Products: day.Products,
        Recipes: convertAssignmentsToRecipes(day.Recipes, recipeIngredientReplacementMode),
    };
};
/**
 * When recipe ingredient replacement mode is enabled, all actions (ie. auto skips & swaps) except must replace are valid.
 * When recipe ingredient replacement mode is disabled, only must replace actions are valid
 *
 * @param recipe
 * @param recipeIngredientReplacementMode
 * @returns
 */
export const filteredRecipeIngredientActions = (recipe, recipeIngredientReplacementMode) => {
    const onlyUseMustReplacements = recipeIngredientReplacementMode === RecipeIngredientReplacementMode.DISABLED;
    if (onlyUseMustReplacements)
        return recipe.RecipeIngredientActions.filter((ria) => ria.IngredientMustReplace);
    else
        return recipe.RecipeIngredientActions;
};
/**
 * My Meals uses the RecipeIngredientActions so we need to map the selectedIngredientOptions on the assignment to RecipeIngredientActions
 */
export const convertSelectionOptionToIngredientAction = (selectedIngredientOptions, quantity, ingredients) => {
    let allIngredientActions = [];
    selectedIngredientOptions.forEach((multipleSelectedOption) => {
        const originalIngredient = ingredients.find((ing) => ing.IngredientId === multipleSelectedOption.originalIngredientId);
        multipleSelectedOption.selectedOptions.forEach((selectedOption) => {
            var _a, _b;
            const selectedIngredient = originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.IngredientOptions.find((io) => io.IngredientId === selectedOption.replacementIngredientId);
            const ingAction = {
                RecipeIngredientId: multipleSelectedOption.recipeIngredientId,
                OriginalIngredientId: multipleSelectedOption.originalIngredientId,
                MustReplaceIngredientCost: selectedOption.replacementPrice,
                IngredientMustReplace: multipleSelectedOption.mustBeReplaced,
                OriginalIngredientName: (_a = originalIngredient === null || originalIngredient === void 0 ? void 0 : originalIngredient.IngredientName) !== null && _a !== void 0 ? _a : "",
                // SubIngredient needs to be set for the modification string to be built correctly
                // e.g w/ Avocado, w/ Chicken or Skip Beef
                SubIngredientId1: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.replacementIngredientId,
                SubIngredientName1: selectedIngredient === null || selectedIngredient === void 0 ? void 0 : selectedIngredient.IngredientName,
                Skip: false,
                Quantity: (_b = selectedOption.ReplacementQty) !== null && _b !== void 0 ? _b : 0,
                Unit: selectedOption.Unit,
            };
            allIngredientActions.push(ingAction);
        });
    });
    return allIngredientActions;
};
/**
 * If an assignment has a quantity greater than 1, we need to break out each assignment.
 *  e.g Assignment with a quantity of 4 will actually be 4 assignments with a quantity of 1. In other words, 4 recipes
 *
 * @param assignment
 * @returns
 */
export const splitAssignments = (assignment) => {
    const arrayOfObjects = Array.from({ length: assignment.Quantity }, () => (Object.assign(Object.assign({}, assignment), { Quantity: 1 })));
    return arrayOfObjects;
};
/**
 * Each recipe assignment needs to be split into its own "Recipe". This is how the existing order update logic works
 *
 * @param recipes
 * @returns
 */
export const convertAssignmentsToRecipes = (recipes, recipeIngredientReplacementMode) => {
    return recipes.flatMap((r) => {
        var _a;
        if (r.Assignments) {
            return (_a = r === null || r === void 0 ? void 0 : r.Assignments) === null || _a === void 0 ? void 0 : _a.flatMap((assignment) => {
                if (assignment.Quantity > 0) {
                    const allAssignments = splitAssignments(assignment);
                    return allAssignments.map((aa) => {
                        const ingredientActions = getAssignmentIngredientActions(aa, r, recipeIngredientReplacementMode);
                        return {
                            RecipeId: r.RecipeId,
                            OriginalQuantity: 1,
                            Quantity: aa.Quantity,
                            ProgramMealId: r.ProgramMealId,
                            RecipeFlags: r.RecipeFlags,
                            RecipeIngredientActions: aa.Quantity > 0 ? ingredientActions : [],
                        };
                    });
                }
                else {
                    const ingredientActions = getAssignmentIngredientActions(assignment, r, recipeIngredientReplacementMode);
                    return {
                        RecipeId: r.RecipeId,
                        OriginalQuantity: 1,
                        Quantity: assignment.Quantity,
                        ProgramMealId: r.ProgramMealId,
                        RecipeFlags: r.RecipeFlags,
                        RecipeIngredientActions: assignment.Quantity > 0 ? ingredientActions : [],
                    };
                }
            });
        }
        else {
            if (r.Quantity > 0) {
                return {
                    ProgramMealId: r.ProgramMealId,
                    Quantity: r.Quantity,
                    OriginalQuantity: r.OriginalQuantity,
                    RecipeId: r.RecipeId,
                    RecipeName: r.RecipeName,
                    RecipeFlags: r.RecipeFlags,
                    RecipeIngredientActions: filteredRecipeIngredientActions(r, recipeIngredientReplacementMode),
                };
            }
        }
        return [];
    });
};
/**
 * Retrieves the ingredient actions for a given assignment
 *
 * @param {RecipeAssignment} assignment - The recipe assignment containing selected ingredient options and quantity.
 * @param {Recipe} recipe - The recipe object containing ingredients and actions.
 * @param {RecipeIngredientReplacementMode} recipeIngredientReplacementMode - The mode defining how ingredient replacements are handled.
 *
 * @returns {RecipeIngredientAction[]} An array of ingredient actions for the assignment, including user customizations and non-customized actions.
 *
 * @description
 * This function processes the ingredient actions for a recipe assignment. It:
 * - Converts the selected ingredient options in the assignment into ingredient actions.
 * - Retrieves non must replace ingredient actions from the recipe (skips and replacements).
 * - Combines user customizations, skips and replacements.
 * - Optionally filters actions based on the `RecipeIngredientReplacementMode`.
 *
 * If `recipeIngredientReplacementMode` is set to `DISABLED`, only user customizations are returned.
 * Otherwise, both user customizations, skips and replacements are included.
 */
const getAssignmentIngredientActions = (assignment, recipe, recipeIngredientReplacementMode) => {
    const actionsFromSelectedOptions = convertSelectionOptionToIngredientAction(assignment.SelectedIngredientOptions, assignment.Quantity, recipe.RecipeIngredients);
    // Get all non must replace actions
    const allNonCustomizedActions = recipe.RecipeIngredientActions.filter((ingAction) => !ingAction.IngredientMustReplace);
    // Combine all skips and replacements with user customizations
    const combinedIngredientActions = [...allNonCustomizedActions, ...actionsFromSelectedOptions];
    // When recipe ingredient replacement mode is disabled, only must replace/customized actions are valid
    const finalIngredientActions = recipeIngredientReplacementMode === RecipeIngredientReplacementMode.DISABLED
        ? actionsFromSelectedOptions
        : combinedIngredientActions;
    return finalIngredientActions;
};
